import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServiceAreasOpen, setIsServiceAreasOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServiceAreasOpen, setIsMobileServiceAreasOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId: string) => {
    // If we're on the home page, just scroll
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
      }
    } else {
      // If we're on another page, navigate to home first, then scroll
      navigate('/');
      setIsMobileMenuOpen(false);
      // Wait for navigation and DOM update before scrolling
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-slate-800/95 backdrop-blur-sm shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/027141891d1cc327d33c10a3e7cd4c2a/fe3505f33eb5b4b78f064f11c2e20413.png"
              alt="Davis County Disaster Cleanup"
              className="h-14 w-auto object-contain transition-all duration-300"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            <Link
              to="/"
              className={`text-sm xl:text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-slate-100 hover:text-sky-400' : 'text-white hover:text-sky-200'
              }`}
            >
              Home
            </Link>
            <button
              onClick={() => scrollToSection('about')}
              className={`text-sm xl:text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-slate-100 hover:text-sky-400' : 'text-white hover:text-sky-200'
              }`}
            >
              About
            </button>
            
            {/* Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <div className="flex items-center space-x-1 cursor-pointer">
                <button
                  onClick={() => scrollToSection('services')}
                  className={`text-sm xl:text-base font-medium transition-colors whitespace-nowrap ${
                    isScrolled ? 'text-slate-100 hover:text-sky-400' : 'text-white hover:text-sky-200'
                  }`}
                >
                  Services
                </button>
                <i
                  className={`ri-arrow-down-s-line text-lg transition-transform ${
                    isServicesOpen ? 'rotate-180' : ''
                  } ${isScrolled ? 'text-slate-100' : 'text-white'}`}
                ></i>
              </div>
              {isServicesOpen && (
                <div className="absolute top-full left-0 pt-2">
                  <div className="w-56 bg-white rounded-lg shadow-lg py-2 border border-gray-100">
                    <Link
                      to="/services/flood-cleanup"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Flood Cleanup
                    </Link>
                    <Link
                      to="/services/sewage-cleanup"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Sewage Cleanup
                    </Link>
                    <Link
                      to="/services/mold-removal"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Mold Removal
                    </Link>
                    <Link
                      to="/services/odor-removal"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Odor Removal
                    </Link>
                    <Link
                      to="/services/smoke-damage"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Smoke Damage
                    </Link>
                    <Link
                      to="/services/storm-damage"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Storm Damage
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Service Areas Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setIsServiceAreasOpen(true)}
              onMouseLeave={() => setIsServiceAreasOpen(false)}
            >
              <div className="flex items-center space-x-1 cursor-pointer">
                <span
                  className={`text-sm xl:text-base font-medium transition-colors whitespace-nowrap ${
                    isScrolled ? 'text-slate-100 hover:text-sky-400' : 'text-white hover:text-sky-200'
                  }`}
                >
                  Service Areas
                </span>
                <i
                  className={`ri-arrow-down-s-line text-lg transition-transform ${
                    isServiceAreasOpen ? 'rotate-180' : ''
                  } ${isScrolled ? 'text-slate-100' : 'text-white'}`}
                ></i>
              </div>
              {isServiceAreasOpen && (
                <div className="absolute top-full left-0 pt-2">
                  <div className="w-48 bg-white rounded-lg shadow-lg py-2 border border-gray-100">
                    <Link
                      to="/bountiful"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Bountiful
                    </Link>
                    <Link
                      to="/centerville"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Centerville
                    </Link>
                    <Link
                      to="/clearfield"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Clearfield
                    </Link>
                    <Link
                      to="/farmington"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Farmington
                    </Link>
                    <Link
                      to="/fruit-heights"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Fruit Heights
                    </Link>
                    <Link
                      to="/kaysville"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Kaysville
                    </Link>
                    <Link
                      to="/layton"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Layton
                    </Link>
                    <Link
                      to="/syracuse"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors whitespace-nowrap"
                    >
                      Syracuse
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection('testimonials')}
              className={`text-sm xl:text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-slate-100 hover:text-sky-400' : 'text-white hover:text-sky-200'
              }`}
            >
              Testimonials
            </button>
            <Link
              to="/blog"
              className={`text-sm xl:text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-slate-100 hover:text-sky-400' : 'text-white hover:text-sky-200'
              }`}
            >
              Blog
            </Link>
            <button
              onClick={() => scrollToSection('contact')}
              className={`text-sm xl:text-base font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-slate-100 hover:text-sky-400' : 'text-white hover:text-sky-200'
              }`}
            >
              Contact
            </button>
            <a
              href="tel:3853406632"
              className="bg-sky-600 text-white px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg text-base font-semibold hover:bg-sky-700 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-phone-fill text-lg"></i>
              <span>Call: (385) 340-6632</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-11 h-11 flex items-center justify-center rounded-lg active:bg-black/10 transition-colors ${
              isScrolled ? 'text-white' : 'text-white'
            }`}
            aria-label="Toggle menu"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-x-0 top-[60px] bottom-0 bg-white overflow-y-auto">
            <div className="flex flex-col px-4 py-4 space-y-1">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="py-3 px-4 text-base font-medium text-gray-700 hover:text-sky-500 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
              >
                Home
              </Link>
              <button
                onClick={() => scrollToSection('about')}
                className="py-3 px-4 text-base font-medium text-gray-700 hover:text-sky-500 hover:bg-gray-50 rounded-lg transition-colors text-left cursor-pointer"
              >
                About
              </button>
              
              {/* Mobile Services Accordion */}
              <div className="rounded-lg overflow-hidden">
                <div
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className="flex items-center justify-between py-3 px-4 cursor-pointer hover:bg-gray-50 rounded-lg"
                >
                  <span className="text-base font-medium text-gray-700">Services</span>
                  <i
                    className={`ri-arrow-down-s-line text-xl text-gray-700 transition-transform ${
                      isMobileServicesOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>
                {isMobileServicesOpen && (
                  <div className="pl-4 flex flex-col bg-gray-50 rounded-lg mx-2 mb-2">
                    <Link
                      to="/services/flood-cleanup"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Flood Cleanup
                    </Link>
                    <Link
                      to="/services/sewage-cleanup"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Sewage Cleanup
                    </Link>
                    <Link
                      to="/services/mold-removal"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Mold Removal
                    </Link>
                    <Link
                      to="/services/odor-removal"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Odor Removal
                    </Link>
                    <Link
                      to="/services/smoke-damage"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Smoke Damage
                    </Link>
                    <Link
                      to="/services/storm-damage"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Storm Damage
                    </Link>
                  </div>
                )}
              </div>

              {/* Mobile Service Areas Accordion */}
              <div className="rounded-lg overflow-hidden">
                <div
                  onClick={() => setIsMobileServiceAreasOpen(!isMobileServiceAreasOpen)}
                  className="flex items-center justify-between py-3 px-4 cursor-pointer hover:bg-gray-50 rounded-lg"
                >
                  <span className="text-base font-medium text-gray-700">Service Areas</span>
                  <i
                    className={`ri-arrow-down-s-line text-xl text-gray-700 transition-transform ${
                      isMobileServiceAreasOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>
                {isMobileServiceAreasOpen && (
                  <div className="pl-4 flex flex-col bg-gray-50 rounded-lg mx-2 mb-2">
                    <Link
                      to="/bountiful"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Bountiful
                    </Link>
                    <Link
                      to="/centerville"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Centerville
                    </Link>
                    <Link
                      to="/clearfield"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Clearfield
                    </Link>
                    <Link
                      to="/farmington"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Farmington
                    </Link>
                    <Link
                      to="/fruit-heights"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Fruit Heights
                    </Link>
                    <Link
                      to="/kaysville"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Kaysville
                    </Link>
                    <Link
                      to="/layton"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Layton
                    </Link>
                    <Link
                      to="/syracuse"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="py-3 px-4 text-base text-gray-600 hover:text-sky-500 transition-colors"
                    >
                      Syracuse
                    </Link>
                  </div>
                )}
              </div>

              <button
                onClick={() => scrollToSection('testimonials')}
                className="py-3 px-4 text-base font-medium text-gray-700 hover:text-sky-500 hover:bg-gray-50 rounded-lg transition-colors text-left cursor-pointer"
              >
                Testimonials
              </button>
              <Link
                to="/blog"
                onClick={() => setIsMobileMenuOpen(false)}
                className="py-3 px-4 text-base font-medium text-gray-700 hover:text-sky-500 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
              >
                Blog
              </Link>
              <button
                onClick={() => scrollToSection('contact')}
                className="py-3 px-4 text-base font-medium text-gray-700 hover:text-sky-500 hover:bg-gray-50 rounded-lg transition-colors text-left cursor-pointer"
              >
                Contact
              </button>
              
              {/* Mobile CTA */}
              <div className="pt-4 px-2 space-y-3">
                <a
                  href="tel:3853406632"
                  className="flex items-center justify-center gap-2 py-3.5 px-6 bg-sky-500 text-white text-base font-semibold rounded-lg hover:bg-sky-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line text-lg"></i>
                  Call (385) 340-6632
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
